/**
 * Swipeable Mixin - Shared swipe behavior for iOS Mail-style swipe-to-reveal
 *
 * This mixin provides common swipe functionality that can be mixed into
 * multiple controllers (ad-swipe, notification-swipe, etc.)
 *
 * Usage in controller:
 *   import { swipeableMixin } from "./mixins/swipeable"
 *   // In connect(): swipeableMixin.setup(this)
 *   // In disconnect(): swipeableMixin.teardown(this)
 *
 * Required targets in controller:
 *   - card: The swipeable content element
 *   - deleteAction: The delete button container (revealed on swipe)
 *
 * Required values in controller:
 *   - threshold: Number (default: 50)
 *   - deleteWidth: Number (default: 80)
 */

// Touch state factory - creates isolated state per controller instance
function createTouchState() {
  return {
    touchStartX: 0,
    touchStartY: 0,
    touchCurrentX: 0,
    isSwiping: false,
    swipeDirectionDetermined: false,
    isRevealed: false,
    boundHandlers: {}
  }
}

// Direction detection constants
const DIRECTION_THRESHOLD_PX = 15
const HORIZONTAL_RATIO = 2

export const swipeableMixin = {
  /**
   * Initialize swipe behavior
   * @param {Controller} controller - Stimulus controller instance
   */
  setup(controller) {
    // Create isolated state for this controller
    controller._swipeState = createTouchState()
    const state = controller._swipeState

    // Bind handlers with controller context
    state.boundHandlers = {
      touchStart: (e) => handleTouchStart(controller, e),
      touchMove: (e) => handleTouchMove(controller, e),
      touchEnd: (e) => handleTouchEnd(controller, e),
      touchCancel: () => handleTouchCancel(controller),
      documentClick: (e) => handleDocumentClick(controller, e)
    }

    // Add touch event listeners
    controller.element.addEventListener('touchstart', state.boundHandlers.touchStart, { passive: false })
    controller.element.addEventListener('touchmove', state.boundHandlers.touchMove, { passive: false })
    controller.element.addEventListener('touchend', state.boundHandlers.touchEnd, { passive: false })
    controller.element.addEventListener('touchcancel', state.boundHandlers.touchCancel)

    // Close swipe when clicking outside
    document.addEventListener('click', state.boundHandlers.documentClick)
  },

  /**
   * Clean up swipe behavior
   * @param {Controller} controller - Stimulus controller instance
   */
  teardown(controller) {
    const state = controller._swipeState
    if (!state?.boundHandlers) return

    controller.element.removeEventListener('touchstart', state.boundHandlers.touchStart)
    controller.element.removeEventListener('touchmove', state.boundHandlers.touchMove)
    controller.element.removeEventListener('touchend', state.boundHandlers.touchEnd)
    controller.element.removeEventListener('touchcancel', state.boundHandlers.touchCancel)
    document.removeEventListener('click', state.boundHandlers.documentClick)

    controller._swipeState = null
  },

  /**
   * Reset card to normal position
   * @param {Controller} controller - Stimulus controller instance
   */
  resetPosition(controller) {
    const transition = 'transform 0.2s ease-out'
    controller.cardTarget.style.transition = transition
    controller.deleteActionTarget.style.transition = transition
    controller.cardTarget.style.transform = 'translateX(0)'
    controller.deleteActionTarget.style.transform = 'translateX(0)'

    if (controller._swipeState) {
      controller._swipeState.isRevealed = false
    }
  }
}

// Private handler functions

function handleTouchStart(controller, event) {
  if (event.touches.length !== 1) return

  const state = controller._swipeState
  const touch = event.touches[0]

  state.touchStartX = touch.clientX
  state.touchStartY = touch.clientY
  state.touchCurrentX = touch.clientX
  state.isSwiping = false
  state.swipeDirectionDetermined = false

  // Remove transition during drag
  controller.cardTarget.style.transition = 'none'
  controller.deleteActionTarget.style.transition = 'none'
}

function handleTouchMove(controller, event) {
  if (event.touches.length !== 1) return

  const state = controller._swipeState
  const touch = event.touches[0]

  state.touchCurrentX = touch.clientX
  const deltaX = state.touchCurrentX - state.touchStartX
  const deltaY = Math.abs(touch.clientY - state.touchStartY)

  // Determine swipe direction (once)
  if (!state.swipeDirectionDetermined && (Math.abs(deltaX) > DIRECTION_THRESHOLD_PX || deltaY > DIRECTION_THRESHOLD_PX)) {
    state.swipeDirectionDetermined = true
    state.isSwiping = Math.abs(deltaX) > deltaY * HORIZONTAL_RATIO
  }

  if (state.isSwiping) {
    event.preventDefault()

    const deleteWidth = controller.deleteWidthValue
    const threshold = controller.thresholdValue

    // If already revealed, allow right swipe to close
    if (state.isRevealed && deltaX > 0) {
      const translateX = Math.min(deltaX - deleteWidth, 0)
      controller.cardTarget.style.transform = `translateX(${translateX}px)`
      controller.deleteActionTarget.style.transform = `translateX(${translateX}px)`
    }
    // Left swipe to reveal
    else if (deltaX < 0) {
      const translateX = Math.max(deltaX, -deleteWidth)
      controller.cardTarget.style.transform = `translateX(${translateX}px)`

      if (Math.abs(translateX) > threshold) {
        controller.deleteActionTarget.style.transform = `translateX(${translateX}px)`
      }
    }
  }
}

function handleTouchEnd(controller, event) {
  const state = controller._swipeState
  const deltaX = state.touchCurrentX - state.touchStartX

  // Re-enable transition
  const transition = 'transform 0.2s ease-out'
  controller.cardTarget.style.transition = transition
  controller.deleteActionTarget.style.transition = transition

  const threshold = controller.thresholdValue
  const deleteWidth = controller.deleteWidthValue

  if (state.isSwiping) {
    if (state.isRevealed && deltaX > threshold) {
      // Close if swiped right while revealed
      swipeableMixin.resetPosition(controller)
    } else if (deltaX < -threshold) {
      // Reveal delete button
      const revealPosition = -deleteWidth
      controller.cardTarget.style.transform = `translateX(${revealPosition}px)`
      controller.deleteActionTarget.style.transform = `translateX(${revealPosition}px)`
      state.isRevealed = true
    } else if (state.isRevealed) {
      // Stay revealed
      const revealPosition = -deleteWidth
      controller.cardTarget.style.transform = `translateX(${revealPosition}px)`
      controller.deleteActionTarget.style.transform = `translateX(${revealPosition}px)`
    } else {
      // Snap back
      swipeableMixin.resetPosition(controller)
    }
  }

  // Prevent link click if swiped
  if (state.isSwiping && Math.abs(deltaX) > 10) {
    event.preventDefault()
    controller.cardTarget.addEventListener('click', preventClick, { once: true })
  }

  state.isSwiping = false
}

function handleTouchCancel(controller) {
  swipeableMixin.resetPosition(controller)
  controller._swipeState.isSwiping = false
}

function handleDocumentClick(controller, event) {
  const state = controller._swipeState
  if (state?.isRevealed && !controller.element.contains(event.target)) {
    swipeableMixin.resetPosition(controller)
  }
}

function preventClick(event) {
  event.preventDefault()
  event.stopPropagation()
}
